import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { GeocodingOverlay } from '@/components/ui/geocoding-overlay';

interface GeocodingProgress {
  id: string;
  current: number;
  total: number;
}

interface GeocodingContextType {
  startGeocoding: (id: string, total: number) => void;
  updateProgress: (id: string, current: number) => void;
  endGeocoding: (id: string) => void;
  isGeocoding: boolean;
  totalProgress: { current: number; total: number };
}

const GeocodingContext = createContext<GeocodingContextType | undefined>(undefined);

export const useGeocoding = () => {
  const context = useContext(GeocodingContext);
  if (!context) {
    throw new Error('useGeocoding must be used within a GeocodingProvider');
  }
  return context;
};

interface GeocodingProviderProps {
  children: React.ReactNode;
}

export const GeocodingProvider: React.FC<GeocodingProviderProps> = ({ children }) => {
  const [progressMap, setProgressMap] = useState<Map<string, GeocodingProgress>>(new Map());
  const progressMapRef = useRef<Map<string, GeocodingProgress>>(new Map());

  const startGeocoding = useCallback((id: string, total: number) => {
    const newProgress: GeocodingProgress = { id, current: 0, total };
    progressMapRef.current.set(id, newProgress);
    setProgressMap(new Map(progressMapRef.current));
  }, []);

  const updateProgress = useCallback((id: string, current: number) => {
    const progress = progressMapRef.current.get(id);
    if (progress) {
      progress.current = current;
      progressMapRef.current.set(id, progress);
      setProgressMap(new Map(progressMapRef.current));
    }
  }, []);

  const endGeocoding = useCallback((id: string) => {
    progressMapRef.current.delete(id);
    setProgressMap(new Map(progressMapRef.current));
  }, []);

  // Calculate total progress across all geocoding operations
  const totalProgress = React.useMemo(() => {
    let current = 0;
    let total = 0;
    
    progressMap.forEach(progress => {
      current += progress.current;
      total += progress.total;
    });
    
    return { current, total };
  }, [progressMap]);

  const isGeocoding = progressMap.size > 0;

  return (
    <GeocodingContext.Provider 
      value={{
        startGeocoding,
        updateProgress,
        endGeocoding,
        isGeocoding,
        totalProgress
      }}
    >
      {children}
      <GeocodingOverlay 
        isActive={isGeocoding}
        current={totalProgress.current}
        total={totalProgress.total}
      />
    </GeocodingContext.Provider>
  );
};